/**
 * BillingOrders — Admin Orders Management UI
 *
 * @see US5, AC-18, AC-19 in docs/specifications/2026-03-08-billing-admin-ui.md
 * @see AC-1 through AC-9 in docs/specifications/2026-04-22-admin-order-creation-ui/spec.md
 */
import React, { useState, useEffect } from "react";
import Layout from "../../../components/Layout";
import { EmptyState } from "../../../components/common/EmptyState";
import { useBillingAdminBrand } from "../../../hooks/useBillingAdminBrand";
import { useBillingOrdersQuery } from "../../../hooks/useAdminProductCatalog";
import BillingBrandGuard from "./BillingBrandGuard";
import BillingBrandSelector from "./BillingBrandSelector";
import {
  CreateOrderModal,
  OrderDetailModal,
  OrderRow,
  OrderTableHead,
} from "./components";
import type { BillingOrder } from "../../../types/adminBilling";

const PAGE_LIMIT = 20;

// =============================================================================
// MAIN PAGE
// =============================================================================

const BillingOrders: React.FC = () => {
  const { selectedBrand, brands, isLoading, selectBrand, isBrandRequired } =
    useBillingAdminBrand();

  const [page, setPage] = useState(0);
  const offset = page * PAGE_LIMIT;

  const { data, isLoading: isLoadingOrders, isError: isErrorOrders, refetch: refetchOrders } = useBillingOrdersQuery(
    selectedBrand?.id ?? null,
    { limit: PAGE_LIMIT, offset }
  );

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const orders = data?.data ?? [];
  const total = data?.total ?? 0;
  const limit = data?.limit ?? PAGE_LIMIT;

  // Reset page when brand changes
  useEffect(() => {
    setPage(0);
  }, [selectedBrand?.id]);

  return (
    <Layout>
      <BillingBrandGuard
        isBrandRequired={isBrandRequired}
        brands={brands}
        isLoading={isLoading}
        onSelectBrand={selectBrand}
      >
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">Zamówienia</h1>
              <p className="text-neutral-600 mt-1 text-sm">
                Zarządzaj zamówieniami i dokumentami billingowymi
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* AC-1: "+ Nowe zamówienie" button */}
              {selectedBrand && (
                <button
                  type="button"
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium transition-colors flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Nowe zamówienie
                </button>
              )}
              <BillingBrandSelector
                brands={brands}
                selectedBrand={selectedBrand}
                onSelectBrand={selectBrand}
              />
            </div>
          </div>

          {/* Table */}
          {isErrorOrders ? (
            <div className="p-6 text-center">
              <p className="text-red-600">Wystąpił błąd podczas ładowania danych.</p>
              <button onClick={() => refetchOrders()} className="mt-2 text-primary-700 underline">
                Spróbuj ponownie
              </button>
            </div>
          ) : isLoadingOrders ? (
            <div className="flex items-center justify-center min-h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="bg-white rounded-lg border border-neutral-200">
              <EmptyState
                title="Brak zamówień"
                description="Nie znaleziono zamówień dla wybranego brandu."
              />
            </div>
          ) : (
            <div className="bg-white shadow-sm rounded-lg border border-neutral-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <OrderTableHead />
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {orders.map((order: BillingOrder) => (
                      <OrderRow
                        key={order.id}
                        order={order}
                        onSelectDetail={setSelectedOrderId}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              {total > limit && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-200 text-sm text-gray-600">
                  <span>
                    Wyświetlanie {offset + 1}-{Math.min(offset + limit, total)} z {total} rekordów
                  </span>
                  <div className="flex gap-2">
                    <button
                      disabled={offset === 0}
                      onClick={() => setPage((p) => p - 1)}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Poprzednia
                    </button>
                    <button
                      disabled={offset + limit >= total}
                      onClick={() => setPage((p) => p + 1)}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Następna
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </BillingBrandGuard>

      {/* Detail Modal */}
      {selectedOrderId && selectedBrand && (
        <OrderDetailModal
          orderId={selectedOrderId}
          brandId={selectedBrand.id}
          onClose={() => setSelectedOrderId(null)}
        />
      )}

      {/* AC-1: Create Order Modal */}
      {showCreateModal && selectedBrand && (
        <CreateOrderModal
          brandId={selectedBrand.id}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            refetchOrders();
          }}
        />
      )}
    </Layout>
  );
};

export default BillingOrders;
